import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Public standalone chat surface loaded inside the embeddable widget iframe.
 * Reads:
 *  - :configId from the URL path
 *  - `color` (accent hex) and `greeting` (first bot message) from ?query
 */
export default function StoreBotWidgetPage() {
  const { configId } = useParams();
  const [search] = useSearchParams();
  const overrideColor = search.get("color");
  const overrideColorSecondary = search.get("color_secondary");
  const overrideColorText = search.get("color_text");
  const overrideGreeting = search.get("greeting");

  const [publicCfg, setPublicCfg] = useState(null);
  const color = overrideColor || publicCfg?.color_primario || "#0ea5e9";
  const colorSecondary = overrideColorSecondary || publicCfg?.color_secundario || "#1d1d1b";
  const colorText = overrideColorText || publicCfg?.color_texto || "#ffffff";
  const avatarUrl = publicCfg?.avatar_url || "";
  const botName = publicCfg?.nombre_bot || "StoreBot";
  const greeting =
    overrideGreeting ||
    publicCfg?.mensaje_bienvenida ||
    "Hola 👋 ¿En qué te puedo ayudar hoy?";

  // Fixed chat background — warm light gray, not configurable.
  const CHAT_BG = "#f5f4f0";

  const [messages, setMessages] = useState([{ role: "assistant", content: greeting }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  // Fetch public config (color + avatar + bot name) once.
  useEffect(() => {
    if (!configId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/storebot/public-config/${encodeURIComponent(configId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPublicCfg(data);
      } catch (_e) {
        /* ignore — widget still works with fallback color/no avatar */
      }
    })();
    return () => { cancelled = true; };
  }, [configId]);

  // Sync the initial greeting bubble when the query greeting changes.
  // Using functional setState + a ref-guarded run keeps this from looping
  // when `messages` changes for any other reason.
  useEffect(() => {
    if (!overrideGreeting) return;
    setMessages((m) =>
      m.length && m[0].role === "assistant" && m[0].content !== overrideGreeting
        ? [{ role: "assistant", content: overrideGreeting }, ...m.slice(1)]
        : m
    );
  }, [overrideGreeting]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    const newHistory = [...messages, { role: "user", content: text }];
    setMessages(newHistory);
    setInput("");
    setSending(true);
    setError("");
    try {
      // Strip the greeting-only assistant message (it's client-side only, not
      // part of the real conversation) so the model gets a clean history.
      const apiMessages = newHistory.filter((m, i) => !(i === 0 && m.role === "assistant"));
      const res = await fetch(`${API}/api/storebot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config_id: configId, messages: apiMessages }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Ocurrió un error");
        setMessages((m) => [...m, { role: "assistant", content: "Lo siento, hubo un problema. Intenta de nuevo en unos segundos." }]);
      } else {
        setMessages((m) => [...m, {
          role: "assistant",
          content: data.reply || "",
          reserva: data.reserva_creada && data.event_link ? { event_link: data.event_link } : null,
        }]);
      }
    } catch (e) {
      setError("Error de conexión");
      setMessages((m) => [...m, { role: "assistant", content: "No pude conectarme al servidor. Revisa tu conexión." }]);
    }
    setSending(false);
  }

  return (
    <div
      className="flex flex-col h-screen"
      style={{ background: CHAT_BG }}
      data-testid="storebot-widget-root"
    >
      {/* Header — uses colorSecondary as background (barra superior only) */}
      <div
        className="px-4 py-4 flex items-center gap-3 shrink-0"
        style={{ background: colorSecondary, color: colorText }}
        data-testid="storebot-widget-header"
      >
        <div
          className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center text-2xl shrink-0"
          style={{ background: avatarUrl ? "#fff" : `${color}33`, border: `2px solid ${color}` }}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={botName} className="w-full h-full object-cover" data-testid="storebot-widget-avatar" />
          ) : (
            <span>🛍️</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base truncate">{botName}</div>
          <div className="text-[12px] opacity-80 flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            En línea · responde en segundos
          </div>
        </div>
      </div>

      {/* Messages — fixed warm light gray background */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-4 space-y-2"
        style={{ background: CHAT_BG }}
        data-testid="storebot-widget-messages"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`storebot-widget-msg-${i}`}
          >
            <div
              className="max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed shadow-sm"
              style={
                m.role === "user"
                  ? { background: color, color: colorText, borderBottomRightRadius: 6 }
                  : { background: "#ffffff", color: "#1f2937", border: "1px solid rgba(0,0,0,0.06)", borderBottomLeftRadius: 6 }
              }
            >
              {m.content}
              {m.reserva?.event_link && (
                <a
                  href={m.reserva.event_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`storebot-widget-reserva-card-${i}`}
                  className="mt-2 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors"
                  style={{ background: "#ecfdf5", border: "1px solid #059669", color: "#065f46" }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <path d="m9 16 2 2 4-4"></path>
                  </svg>
                  Reserva agendada · Agregar a mi Google Calendar
                </a>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="storebot-widget-typing">
            <div className="rounded-2xl px-3 py-2 shadow-sm" style={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.06)" }}>
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: color, animationDelay: "0ms" }}></span>
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: color, animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: color, animationDelay: "300ms" }}></span>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="text-center text-xs text-red-600 mt-2" data-testid="storebot-widget-error">{error}</div>
        )}
      </div>

      {/* Composer — same warm bg as messages so it feels connected */}
      <div
        className="p-2 shrink-0"
        style={{ background: CHAT_BG, borderTop: "1px solid rgba(0,0,0,0.08)" }}
        data-testid="storebot-widget-composer"
      >
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            placeholder="Escribe tu mensaje..."
            disabled={sending}
            data-testid="storebot-widget-input"
            className="flex-1 rounded-full px-4 py-2 text-sm focus:outline-none"
            style={{
              background: "#ffffff",
              color: "#1f2937",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          />
          <button
            onClick={send}
            disabled={sending || !input.trim()}
            data-testid="storebot-widget-send"
            className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40 transition-opacity"
            style={{ background: color, color: colorText }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
        <div className="text-center text-[10px] mt-1" style={{ color: "#6b7280" }}>
          Powered by{" "}
          <a href="https://acbothub.com" target="_blank" rel="noopener noreferrer" className="hover:underline">
            ACBotHub
          </a>
        </div>
      </div>
    </div>
  );
}
